/**
 * Utilities for working with place working hours
 */

/**
 * Convert workingHours array to object format
 * @param {Array|Object} workingHours - Array or object of working hours from API
 * @returns {Object} - Working hours object indexed by day
 */
const normalizeWorkingHours = (workingHours) => {
  if (!workingHours) return null;

  // If already an object (JSON format), return as-is
  if (!Array.isArray(workingHours) && typeof workingHours === 'object') {
    return workingHours;
  }

  // Convert array (component format) to object
  if (Array.isArray(workingHours)) {
    const normalized = {};
    workingHours.forEach((item) => {
      if (item.day) {
        normalized[item.day] = {
          open: item.open,
          close: item.close,
          isClosed: item.isClosed || false,
        };
      }
    });
    return normalized;
  }

  return null;
};

/**
 * Get current day of week in working hours format
 * @returns {string} - Day name (e.g., 'monday')
 */
export const getCurrentDay = () => {
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return days[new Date().getDay()];
};

/**
 * Get day name in Russian
 * @param {string} day - Day in English (e.g., 'monday')
 * @returns {string} - Day name in Russian
 */
export const getDayName = (day) => {
  const names = {
    monday: 'Пн',
    tuesday: 'Вт',
    wednesday: 'Ср',
    thursday: 'Чт',
    friday: 'Пт',
    saturday: 'Сб',
    sunday: 'Вс',
  };
  return names[day] || day;
};

/**
 * Get full day name in Russian
 * @param {string} day - Day in English
 * @returns {string} - Full day name in Russian
 */
export const getFullDayName = (day) => {
  const names = {
    monday: 'Понедельник',
    tuesday: 'Вторник',
    wednesday: 'Среда',
    thursday: 'Четверг',
    friday: 'Пятница',
    saturday: 'Суббота',
    sunday: 'Воскресенье',
  };
  return names[day] || day;
};

/**
 * Parse time string to minutes since midnight
 * @param {string} time - Time in HH:MM format
 * @returns {number} - Minutes since midnight
 */
const parseTime = (time) => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

/**
 * Get current time in minutes since midnight
 * @returns {number} - Minutes since midnight
 */
const getCurrentTimeMinutes = () => {
  const now = new Date();
  return now.getHours() * 60 + now.getMinutes();
};

/**
 * Check if place is currently open
 * @param {Object|Array} workingHours - Working hours object or array
 * @returns {boolean} - True if open now
 */
export const isOpenNow = (workingHours) => {
  if (!workingHours) return false;

  const normalized = normalizeWorkingHours(workingHours);
  if (!normalized) return false;

  const currentDay = getCurrentDay();
  const schedule = normalized[currentDay];

  if (!schedule || schedule.isClosed) return false;

  const currentTimeMinutes = getCurrentTimeMinutes();
  const openTimeMinutes = parseTime(schedule.open);
  let closeTimeMinutes = parseTime(schedule.close);

  // Handle closing after midnight
  if (closeTimeMinutes < openTimeMinutes) {
    closeTimeMinutes += 24 * 60;
  }

  return currentTimeMinutes >= openTimeMinutes && currentTimeMinutes < closeTimeMinutes;
};

/**
 * Get closing time for today
 * @param {Object|Array} workingHours - Working hours object or array
 * @returns {string|null} - Closing time or null if closed
 */
export const getClosingTime = (workingHours) => {
  if (!workingHours) return null;

  const normalized = normalizeWorkingHours(workingHours);
  if (!normalized) return null;

  const currentDay = getCurrentDay();
  const schedule = normalized[currentDay];

  if (!schedule || schedule.isClosed) return null;

  return schedule.close;
};

/**
 * Get opening time for today or next open day
 * @param {Object|Array} workingHours - Working hours object or array
 * @returns {Object|null} - { time: string, day: string } or null
 */
export const getOpeningTime = (workingHours) => {
  if (!workingHours) return null;

  const normalized = normalizeWorkingHours(workingHours);
  if (!normalized) return null;

  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const currentDay = getCurrentDay();
  const currentDayIndex = days.indexOf(currentDay);
  const currentTimeMinutes = getCurrentTimeMinutes();

  // Check if opens later today
  const todaySchedule = normalized[currentDay];
  if (todaySchedule && !todaySchedule.isClosed) {
    const openTimeMinutes = parseTime(todaySchedule.open);
    if (currentTimeMinutes < openTimeMinutes) {
      return { time: todaySchedule.open, day: 'today' };
    }
  }

  // Find next open day
  for (let i = 1; i <= 7; i++) {
    const nextDayIndex = (currentDayIndex + i) % 7;
    const nextDay = days[nextDayIndex];
    const schedule = normalized[nextDay];

    if (schedule && !schedule.isClosed) {
      return {
        time: schedule.open,
        day: i === 1 ? 'tomorrow' : getDayName(nextDay),
      };
    }
  }

  return null;
};

/**
 * Get current day status with message and color
 * @param {Object|Array} workingHours - Working hours object or array
 * @returns {Object} - { status, message, color }
 *   color — CSS-переменная, а не литерал: подставляется в style и следует
 *   за темой. Семантика по BRANDBOOK.md: success/warning/danger.
 */
export const getCurrentDayStatus = (workingHours) => {
  if (!workingHours) {
    return {
      status: 'unknown',
      message: 'Часы работы не указаны',
      color: 'var(--color-text-secondary)',
    };
  }

  const normalized = normalizeWorkingHours(workingHours);
  if (!normalized) {
    return {
      status: 'unknown',
      message: 'Часы работы не указаны',
      color: 'var(--color-text-secondary)',
    };
  }

  const currentDay = getCurrentDay();
  const schedule = normalized[currentDay];

  // Closed today
  if (!schedule || schedule.isClosed) {
    const opening = getOpeningTime(workingHours);
    if (opening) {
      if (opening.day === 'tomorrow') {
        return {
          status: 'opens-tomorrow',
          message: `Откроется завтра в ${opening.time}`,
          color: 'var(--color-text-secondary)',
        };
      }
      return {
        status: 'closed',
        message: 'Сегодня выходной',
        color: 'var(--color-danger)',
      };
    }
    return {
      status: 'closed',
      message: 'Сегодня выходной',
      color: 'var(--color-danger)',
    };
  }

  const currentTimeMinutes = getCurrentTimeMinutes();
  const openTimeMinutes = parseTime(schedule.open);
  let closeTimeMinutes = parseTime(schedule.close);

  // Handle closing after midnight
  if (closeTimeMinutes < openTimeMinutes) {
    closeTimeMinutes += 24 * 60;
  }

  // Check if 24/7 (00:00 to 00:00)
  if (schedule.open === '00:00' && schedule.close === '00:00') {
    return {
      status: 'open',
      message: 'Открыто круглосуточно',
      color: 'var(--color-success)',
    };
  }

  // Currently open
  if (currentTimeMinutes >= openTimeMinutes && currentTimeMinutes < closeTimeMinutes) {
    return {
      status: 'open',
      message: `Сегодня открыто до ${schedule.close}`,
      color: 'var(--color-success)',
    };
  }

  // Opens later today
  if (currentTimeMinutes < openTimeMinutes) {
    return {
      status: 'opens-today',
      message: `Откроется в ${schedule.open}`,
      color: 'var(--color-warning)',
    };
  }

  // Already closed for today
  const opening = getOpeningTime(workingHours);
  if (opening && opening.day === 'tomorrow') {
    return {
      status: 'opens-tomorrow',
      message: `Откроется завтра в ${opening.time}`,
      color: 'var(--color-text-secondary)',
    };
  }

  return {
    status: 'closed',
    message: 'Закрыто',
    color: 'var(--color-text-secondary)',
  };
};

/**
 * Format working hours for display
 * @param {Object|Array} workingHours - Working hours object or array
 * @returns {Array} - Array of formatted days
 */
export const formatWorkingHours = (workingHours) => {
  if (!workingHours) return [];

  const normalized = normalizeWorkingHours(workingHours);
  if (!normalized) return [];

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const currentDay = getCurrentDay();

  return days.map((day) => {
    const schedule = normalized[day];
    const isClosed = !schedule || schedule.isClosed;
    let hours = 'Выходной';

    if (schedule && !schedule.isClosed) {
      // Check if 24/7
      if (schedule.open === '00:00' && schedule.close === '00:00') {
        hours = 'Круглосуточно';
      } else {
        hours = `${schedule.open} - ${schedule.close}`;
      }
    }

    return {
      day,
      dayName: getFullDayName(day),
      isToday: day === currentDay,
      isClosed,
      hours,
    };
  });
};
